import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Shield, Key, AlertTriangle, CheckCircle, Clock } from 'lucide-react';

export function SecuritySettings() {
  const [autoLock, setAutoLock] = React.useState(true);
  const [lockTimeout, setLockTimeout] = React.useState('15');
  const [transactionLimits, setTransactionLimits] = React.useState(true);
  const [multiConfirm, setMultiConfirm] = React.useState(false);
  const [phishingProtection, setPhishingProtection] = React.useState(true);

  // Mock security status
  const securityScore = 85;
  const connectedDApps = [
    { name: 'Uniswap V3', lastUsed: '2 hours ago', permissions: ['Token approvals', 'Transaction signing'] },
    { name: 'OpenSea', lastUsed: '1 day ago', permissions: ['NFT interactions'] },
    { name: 'Compound', lastUsed: '3 days ago', permissions: ['Lending protocol'] },
  ];

  return (
    <div className="space-y-6">
      {/* Security Score */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security Score
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-3xl font-bold text-green-600">{securityScore}/100</div>
              <div className="text-sm text-slate-500 dark:text-slate-400">Your wallet security level</div>
            </div>
            <Badge variant={securityScore >= 80 ? 'default' : 'secondary'} className="bg-green-100 text-green-800">
              {securityScore >= 80 ? 'Strong' : 'Fair'}
            </Badge>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Strong password protection</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Auto-lock enabled</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <span>Consider enabling transaction limits</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Wallet Security */}
      <Card>
        <CardHeader>
          <CardTitle>Wallet Security</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Auto-lock Wallet</Label>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Automatically lock wallet after inactivity
              </div>
            </div>
            <Switch checked={autoLock} onCheckedChange={setAutoLock} />
          </div>

          {autoLock && (
            <div className="space-y-2">
              <Label>Lock Timeout (minutes)</Label>
              <Input
                type="number"
                value={lockTimeout}
                onChange={(e) => setLockTimeout(e.target.value)}
                min="1"
                max="60"
                className="w-32"
              />
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Phishing Protection</Label>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Warn about suspicious websites and transactions
              </div>
            </div>
            <Switch checked={phishingProtection} onCheckedChange={setPhishingProtection} />
          </div>
        </CardContent>
      </Card>

      {/* Transaction Security */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction Security</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Transaction Limits</Label>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Set daily limits for large transactions
              </div>
            </div>
            <Switch checked={transactionLimits} onCheckedChange={setTransactionLimits} />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Multi-confirmation</Label>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Require additional confirmation for large amounts
              </div>
            </div>
            <Switch checked={multiConfirm} onCheckedChange={setMultiConfirm} />
          </div>

          {transactionLimits && (
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-3">
              <h4 className="font-medium">Daily Limits</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Per Transaction ($)</Label>
                  <Input placeholder="5000" />
                </div>
                <div>
                  <Label>Daily Total ($)</Label>
                  <Input placeholder="25000" />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connected DApps */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Connected Applications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {connectedDApps.map((dapp, index) => (
            <div key={index} className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
              <div>
                <div className="font-medium">{dapp.name}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Last used: {dapp.lastUsed}
                </div>
                <div className="flex gap-1 mt-2">
                  {dapp.permissions.map((permission, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {permission}
                    </Badge>
                  ))}
                </div>
              </div>
              <Button variant="outline" size="sm">
                Revoke
              </Button>
            </div>
          ))}
          
          <Button variant="outline" className="w-full">
            Revoke All Connections
          </Button>
        </CardContent>
      </Card>

      {/* Emergency Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Emergency Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <h4 className="font-medium text-red-800 dark:text-red-300 mb-2">
              Emergency Procedures
            </h4>
            <p className="text-sm text-red-700 dark:text-red-400 mb-4">
              Use these actions only if you suspect your wallet has been compromised.
            </p>
            <div className="space-y-2">
              <Button variant="outline" className="w-full border-red-300 text-red-700 hover:bg-red-50">
                Lock Wallet Immediately
              </Button>
              <Button variant="outline" className="w-full border-red-300 text-red-700 hover:bg-red-50">
                Revoke All Token Approvals
              </Button>
              <Button variant="destructive" className="w-full">
                Emergency Transfer to Safe Address
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}